import { z } from "zod";

/**
 * Phone validation schema - E.164 format
 * Supports +1 (US/Canada) and +55 (Brazil) formats
 * Validates based on country prefix
 */
export const PhoneSchema = z
  .string()
  .min(1, "Phone number is required")
  .refine(
    (phone) => {
      // Extract digits only
      const digits = phone.replace(/\D/g, "");
      
      // If phone includes prefix (starts with +), validate total length
      if (phone.startsWith("+")) {
        // +55: total should be 13 digits (2 for country + 11 for number)
        if (phone.startsWith("+55")) {
          return digits.length === 13;
        }
        // +1: total should be 11 digits (1 for country + 10 for number)
        if (phone.startsWith("+1")) {
          return digits.length === 11;
        }
        // Other countries: at least 8 digits for national number
        // Extract country code (1-3 digits after +)
        const match = phone.match(/^\+(\d{1,3})(\d+)$/);
        if (match) {
          const countryCode = match[1];
          const nationalNumber = match[2].replace(/\D/g, "");
          return nationalNumber.length >= 8;
        }
      }
      
      // If no prefix, validate as national number
      // +55: 11 digits, +1: 10 digits, others: at least 8
      return digits.length >= 8;
    },
    { message: "Phone number format is invalid" }
  )
  .transform((phone) => {
    // Normalize to E.164 format
    const digits = phone.replace(/\D/g, "");
    if (phone.startsWith("+55")) {
      // Ensure exactly 11 digits for national number
      const nationalNumber = digits.slice(-11);
      return `+55${nationalNumber}`;
    } else if (phone.startsWith("+1")) {
      // Ensure exactly 10 digits for national number
      const nationalNumber = digits.slice(-10);
      return `+1${nationalNumber}`;
    } else if (phone.startsWith("+")) {
      // Keep prefix and national number
      return phone;
    }
    // If no prefix, assume +1 (US/Canada)
    const nationalNumber = digits.slice(-10);
    return `+1${nationalNumber}`;
  });

/**
 * Email validation schema with XSS sanitization
 */
export const EmailSchema = z
  .string()
  .min(1, "Email is required")
  .email("Please enter a valid email address")
  .max(255, "Email must be less than 255 characters")
  .transform((email) => {
    // Basic XSS sanitization - remove potentially dangerous characters
    return email.trim().toLowerCase().replace(/[<>]/g, "");
  });

/**
 * Password validation schema - strong password requirements
 */
export const PasswordSchema = z
  .string()
  .min(8, "Password must be at least 8 characters")
  .max(128, "Password must be less than 128 characters")
  .regex(
    /(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
    "Password must contain at least one uppercase letter, one lowercase letter, and one number"
  )
  .refine(
    (password) => !/(.)\1{3,}/.test(password),
    { message: "Password cannot contain the same character repeated 4+ times" }
  );

/**
 * Name validation schema
 */
export const NameSchema = z
  .string()
  .min(5, "Name must be at least 5 characters")
  .max(100, "Name must be less than 100 characters")
  .transform((name) => {
    // Sanitize name - remove HTML tags and dangerous characters
    return name.trim().replace(/[<>]/g, "").replace(/\s+/g, " ");
  });

/**
 * Address validation schema
 */
export const AddressSchema = z
  .string()
  .min(5, "Address must be at least 5 characters")
  .max(200, "Address must be less than 200 characters")
  .transform((address) => {
    // Sanitize address
    return address.trim().replace(/[<>]/g, "").replace(/\s+/g, " ");
  });

/**
 * Postal code validation schema
 * Accepts minimum 3 characters, supports Canadian format (A1A 1A1) and other formats
 */
export const PostalCodeSchema = z
  .string()
  .min(3, "Postal code must be at least 3 characters")
  .max(10, "Postal code must be less than 10 characters")
  .refine(
    (code) => {
      const cleaned = code.replace(/\s/g, "").toUpperCase();
      // If 6 characters, validate Canadian format (A1A1A1)
      if (cleaned.length === 6) {
        return /^[A-Z]\d[A-Z]\d[A-Z]\d$/.test(cleaned);
      }
      // Otherwise, accept alphanumeric with minimum 3 characters
      return /^[A-Z0-9\s-]{3,}$/i.test(code);
    },
    { message: "Postal code format is invalid" }
  )
  .transform((code) => {
    // Normalize to uppercase
    const cleaned = code.replace(/\s/g, "").toUpperCase();
    // If Canadian format (6 alphanumeric), add space
    if (cleaned.length === 6 && /^[A-Z]\d[A-Z]\d[A-Z]\d$/.test(cleaned)) {
      return `${cleaned.slice(0, 3)} ${cleaned.slice(3)}`;
    }
    // Otherwise, return cleaned version
    return cleaned;
  });

/**
 * Promo code validation schema
 */
export const PromoCodeSchema = z
  .string()
  .min(3, "Promo code must be at least 3 characters")
  .max(20, "Promo code must be less than 20 characters")
  .regex(
    /^[A-Z0-9_-]+$/,
    "Promo code can only contain uppercase letters, numbers, hyphens, and underscores"
  )
  .transform((code) => code.trim().toUpperCase());

/**
 * Date validation schema (YYYY-MM-DD format)
 */
export const DateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "Date must be in YYYY-MM-DD format")
  .refine(
    (date) => {
      const dateObj = new Date(date + "T12:00:00");
      return !isNaN(dateObj.getTime());
    },
    { message: "Invalid date" }
  )
  .refine(
    (date) => {
      const dateObj = new Date(date + "T12:00:00");
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return dateObj >= today;
    },
    { message: "Date cannot be in the past" }
  );

/**
 * Time validation schema
 */
export const TimeSchema = z.object({
  hour: z.number().min(1).max(12),
  minute: z.number().min(0).max(59).refine((m) => m % 15 === 0, {
    message: "Minute must be in 15-minute increments",
  }),
  period: z.enum(["am", "pm"]),
});

/**
 * Notes/Additional information schema
 */
export const NotesSchema = z
  .string()
  .max(1000, "Notes must be less than 1000 characters")
  .transform((notes) => {
    // Sanitize notes - remove HTML tags
    return notes.trim().replace(/<[^>]*>/g, "");
  })
  .optional()
  .default("");

/**
 * Complete booking input schema
 */
export const BookingInputSchema = z.object({
  fullName: NameSchema,
  phonePrefix: z.string().regex(/^\+\d{1,3}$/, "Invalid phone prefix"),
  phoneCountryCode: z.string().optional(),
  phone: PhoneSchema,
  email: EmailSchema,
  password: PasswordSchema,
  confirmPassword: z.string(),
  addressLine1: AddressSchema,
  city: z.string().min(1, "City is required").max(100, "City must be less than 100 characters"),
  postalCode: PostalCodeSchema,
  addressLine2: z.string().max(100, "Address line 2 must be less than 100 characters").optional().default(""),
  date: DateSchema,
  time: TimeSchema,
  notes: NotesSchema,
  lat: z.number().optional(),
  lng: z.number().optional(),
  serviceZone: z.enum(["FRZ", "SRZ", "OUT_OF_COVERAGE"]).optional(),
}).refine(
  (data) => data.password === data.confirmPassword,
  {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  }
);

/**
 * Profile update schema
 */
export const ProfileUpdateSchema = z.object({
  fullName: NameSchema.optional(),
  email: EmailSchema.optional(),
  phone: PhoneSchema.optional(),
  addressLine1: AddressSchema.optional(),
  city: z.string().max(100).optional(),
  postalCode: PostalCodeSchema.optional(),
  addressLine2: z.string().max(100).optional(),
});

/**
 * Payment data schema
 */
export const PaymentDataSchema = z.object({
  bookingId: z.string().uuid("Invalid booking ID"),
  amount: z.number().positive("Amount must be positive"),
  currency: z.string().default("cad"),
  promoCode: PromoCodeSchema.optional().nullable(),
});

